// Marketing home / landing page ("/"). Rendered inside the shared SiteChrome (header + footer).
// Its styles live in index.css, scoped under `.lp` so the design's resets / utility class names
// never leak into the rest of the app.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import SkillCarousel from "@/components/SkillCarousel";
import { useSeoMeta } from "@/lib/useSeoMeta";

// ── icons (inline, matching the mockup) ──────────────────────────────────────
const SearchIcon = ({ c = "#727375" }: { c?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
);
const CheckCircle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
);
const Shield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="9 18 15 12 9 6" /></svg>
);
const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="6 9 12 15 18 9" /></svg>
);

// "How it works" — the three ways to use SkillPatch, as interactive tabs (ported from Home.tsx).
const TABS = [
  {
    label: "Discover",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
    steps: [
      { tag: "Search", title: "Browse the registry", body: "Search by name, tag, or use-case. Filter by verified, rating, or community." },
      { tag: "Install", title: "One curl command", body: "Copy the install snippet and you're done. No configuration required." },
      { tag: "Use", title: "Slash command ready", body: "Open Agent session, type /skillpatch, and the skill is right there." },
    ],
  },
  {
    label: "Publish",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>,
    steps: [
      { tag: "Setup", title: "Connect from Settings", body: "Grab your setup command from Settings and run it once — SkillPatch is live in your session." },
      { tag: "Capture", title: "Save your session", body: "Run /skillpatch save at any point. Agent packages it automatically." },
      { tag: "Review", title: "Automated quality check", body: "Security scan, structure validation, and quality scoring — all before human eyes." },
      { tag: "Publish", title: "Live on the registry", body: "Approve and publish. Versioned, searchable, installable by anyone." },
    ],
  },
  {
    label: "Suggest",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>,
    steps: [
      { tag: "Sign in", title: "Log in to SkillPatch", body: "Sign in on the web, or connect Agent with your API key from Settings." },
      { tag: "Suggest", title: "Plain-English improvement", body: "Describe the change. From inside Agent or from the web." },
      { tag: "AI Review", title: "Automated validation", body: "The suggestion is reviewed for correctness, safety, and usefulness before the owner sees it." },
      { tag: "Merge", title: "Owner approves", body: "If the owner accepts, the change ships in a new version with your credit." },
    ],
  },
];

// "Claude Code integration" setup steps (ported from Home.tsx).
const SETUP_STEPS = [
  { n: "1", title: "Create a free account", body: "Sign up in seconds — no credit card needed." },
  { n: "2", title: "Go to Settings", body: "Your personal setup command is waiting, already configured with your API key." },
  { n: "3", title: "Copy and run", body: "Paste one line into your terminal. SkillPatch is live inside Agent session." },
];

// The six security dimensions every skill is scored on (sample "strong" scores for the marketing
// radar). Order matches the radar vertices, clockwise from the top.
const VERIFY_DIMS = [
  { label: "Prompt Injection", score: 96, desc: "Resists hidden instructions smuggled through inputs or tool output." },
  { label: "Data Exfiltration", score: 72, desc: "No unauthorized sending of your code, files, or secrets anywhere." },
  { label: "Code Execution", score: 92, desc: "Shell and eval usage is scoped, expected, and safe." },
  { label: "Supply Chain", score: 72, desc: "Dependencies and sources are vetted for tampering." },
  { label: "Auth & Secrets", score: 95, desc: "No leaked API keys, tokens, or hard-coded credentials." },
  { label: "Network Safety", score: 72, desc: "Outbound connections are declared and match the stated purpose." },
];
// SYNC: mirror any change here into `faq` in public/seo-manifest.json — the backend renders these
// questions into the homepage's server-side HTML and FAQPage JSON-LD (see services/seo.py), which
// is the copy Google reads before it runs any JavaScript.
const FAQ = [
  { q: "What are agent skills?", a: "Skills are reusable instruction packages with SKILL.md plus any helper files that teach an AI coding agent how to do a specific task, like reviewing a pull request or scaffolding tests. Once installed, your agent picks up the skill automatically whenever it's relevant." },
  { q: "Is SkillPatch free to use?", a: "Creating an account and installing public skills is free. SkillPatch is an independent registry built on the open SKILL.md standard, and we plan to keep it free for the community." },
  { q: "How do I install a skill from SkillPatch?", a: "Open any skill, copy its one-line install command (or the ready-made prompt for your agent), and run it in your terminal. It downloads straight into your project's skills folder — no build step and no configuration." },
  { q: "Which coding agents does SkillPatch support?", a: "Any agent that follows the open SKILL.md standard — including Claude Code, Cursor, GitHub Copilot, Codex, Antigravity, OpenClaw, OpenCode, and LatentCode. The installer places each skill in that agent's native skills directory." },
  { q: "Are the skills safe to use?", a: "Every skill runs through an automated Scan → Security review before it goes live, checking for leaked secrets, dangerous commands, and structural issues. Each skill page shows its security report and community rating, so you always know what you're installing." },
  { q: "Can I create and share my own skills?", a: "Yes. Run /skillpatch save in your session, or upload a folder or archive on the web, and SkillPatch packages, reviews, and publishes it for you. Keep it private for yourself or your team, or make it public whenever you're ready." },
  { q: "How do skills improve over time?", a: "Anyone can suggest an improvement, in plain text or as an edited version. Each suggestion is automatically reviewed for correctness and safety, then the owner approves it changes ship as a new version with the contributor credited, and full history means nothing silently breaks." },
  { q: "How does SkillPatch help Claude Code users?", a: "SkillPatch turns the workflows you repeat in Claude Code into reusable skills. Connect once from Settings, then use /skillpatch right inside your session to browse and install community skills, save your own, and suggest improvements. so you stop re-typing the same prompts and start building on work that's already been done." },
];

// Flowing dot along the Publish pipeline connectors (ported from Home.tsx).
function FlowDot({ delay = 0 }: { delay?: number }) {
  return (
    <span
      className="animate-flow-right"
      style={{ position: "absolute", width: 8, height: 8, borderRadius: 9999, background: "var(--brand)", boxShadow: "0 0 8px var(--brand)", animationDelay: `${delay}s`, top: "calc(50% - 4px)" }}
    />
  );
}

// Right-hand visual panel for the active "how it works" tab (ported from Home.tsx).
function TabVisual({ tabIdx }: { tabIdx: number }) {
  if (tabIdx === 0) {
    return (
      <div className="relative overflow-hidden rounded-[16px]" style={{ border: "1px solid var(--border)", borderTopColor: "var(--border-strong)", background: "var(--glass-strong)" }}>
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2 rounded-[8px] px-3 py-2" style={{ border: "1px solid var(--border-strong)", background: "var(--glass)" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--text-muted)" }}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <span className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>Search skills…</span>
          </div>
        </div>
        <div className="divide-y" style={{ borderColor: "var(--border)" }}>
          {[
            { name: "git-reviewer", desc: "AI-powered code review", tag: "git" },
            { name: "test-writer", desc: "Auto-generate test suites", tag: "testing" },
            { name: "doc-builder", desc: "Generate docs from code", tag: "docs" },
          ].map((skill) => (
            <div key={skill.name} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-mono text-xs font-medium" style={{ color: "var(--text)" }}>{skill.name}</p>
                <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>{skill.desc}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-[3px] border border-primary/25 bg-primary/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-primary">{skill.tag}</span>
                <span className="flex items-center gap-1 text-[11px]" style={{ color: "var(--brand)" }}>★ 4.8</span>
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3" style={{ borderTop: "1px solid var(--border)", background: "var(--glass)" }}>
          <p className="font-mono text-[10px] mb-1" style={{ color: "var(--text-muted)" }}>$ install</p>
          <p className="font-mono text-[11px]">
            <span style={{ color: "var(--brand-amber)" }}>curl</span>
            <span style={{ color: "var(--text-muted)" }}> -sSL </span>
            <span style={{ color: "var(--brand)" }}>…/git-reviewer</span>
            <span style={{ color: "var(--text-muted)" }}> | tar -xz</span>
          </p>
        </div>
      </div>
    );
  }

  if (tabIdx === 1) {
    const stages = ["Write", "Review", "Verify", "Publish"];
    return (
      <div className="rounded-[16px] p-6" style={{ border: "1px solid var(--border)", background: "var(--bg-elev)" }}>
        <p className="eyebrow mb-6" style={{ justifyContent: "center" }}>Publishing pipeline</p>
        <div className="flex items-center justify-between gap-0">
          {stages.map((stage, i) => (
            <div key={stage} className="flex flex-1 items-center">
              <div className="flex flex-1 flex-col items-center">
                <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-[10px]" style={{ border: stage === "Publish" ? "1px solid var(--brand)" : "1px solid var(--border)", background: stage === "Publish" ? "var(--brand-tint)" : "var(--bg)", boxShadow: stage === "Publish" ? "0 0 16px var(--brand-ring)" : undefined }}>
                  {stage === "Publish" && <span className="animate-scan" style={{ position: "absolute", left: 0, right: 0, height: 2, background: "var(--brand)", opacity: 0.7 }} />}
                  <span className="relative z-10 font-mono text-[10px] font-medium" style={{ color: stage === "Publish" ? "var(--brand)" : "var(--text-muted)" }}>{i + 1}</span>
                </div>
                <p className="mt-2 font-mono text-[11px]" style={{ color: stage === "Publish" ? "var(--brand)" : "var(--text-muted)" }}>{stage}</p>
              </div>
              {i < stages.length - 1 && (
                <div className="relative mx-1 h-px flex-1" style={{ background: "var(--border-strong)" }}>
                  <FlowDot delay={i * 0.5} />
                  <FlowDot delay={i * 0.5 + 1.0} />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center gap-3 rounded-[8px] px-4 py-3" style={{ background: "var(--bg-subtle)", border: "1px solid var(--border)" }}>
          <span className="h-2 w-2 rounded-full bg-green-400" />
          <span className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>Automated review passed · Ready to publish</span>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[16px]" style={{ border: "1px solid var(--border)", background: "var(--bg-elev)" }}>
      <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="flex gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}>U</div>
          <div className="flex-1 rounded-[8px] px-3 py-2" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>"The reviewer should also check for unused imports and suggest removals."</p>
          </div>
        </div>
      </div>
      <div className="divide-y" style={{ borderColor: "var(--border)" }}>
        {[
          { agent: "Quality Check", verdict: "Relevant improvement", ok: true },
          { agent: "Security Scan", verdict: "No risks detected", ok: true },
          { agent: "Owner Review", verdict: "Awaiting approval", ok: null as boolean | null },
        ].map((r) => (
          <div key={r.agent} className="flex items-center justify-between px-4 py-2.5">
            <p className="font-mono text-xs" style={{ color: "var(--text)" }}>{r.agent}</p>
            <span className="rounded-full px-2 py-0.5 font-mono text-[10px]" style={{ background: r.ok === true ? "rgba(34,197,94,0.12)" : "var(--brand-tint)", color: r.ok === true ? "#22c55e" : "var(--brand)" }}>{r.verdict}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Standalone spider/radar of the six security dimensions (theme-aware via the app's --radar-* vars).
function VerifyRadar({ size = 320 }: { size?: number }) {
  const pts = VERIFY_DIMS;
  const n = pts.length;
  const cx = size / 2, cy = size / 2, R = size * 0.32;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const at = (i: number, r: number): [number, number] => [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  const ring = (r: number) => pts.map((_, i) => at(i, r).join(",")).join(" ");
  const dataPoly = pts.map((d, i) => at(i, (R * d.score) / 100).join(",")).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block h-auto w-full max-w-[340px] overflow-visible">
      <defs>
        <linearGradient id="lp-radar-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--radar-fill-0)" stopOpacity={0.42} />
          <stop offset="100%" stopColor="var(--radar-fill-1)" stopOpacity={0.16} />
        </linearGradient>
        <linearGradient id="lp-radar-stroke" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--radar-stroke-0)" />
          <stop offset="100%" stopColor="var(--radar-stroke-1)" />
        </linearGradient>
      </defs>
      {/* grid rings + spokes */}
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <polygon key={f} points={ring(R * f)} fill="none" style={{ stroke: "var(--radar-grid)" }} strokeWidth={0.8} />
      ))}
      {pts.map((_, i) => { const [x, y] = at(i, R); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} style={{ stroke: "var(--radar-grid-soft)" }} strokeWidth={1} />; })}
      {/* score polygon */}
      <polygon points={dataPoly} fill="url(#lp-radar-fill)" stroke="url(#lp-radar-stroke)" strokeWidth={2.5} strokeLinejoin="round" />
      {pts.map((d, i) => { const [x, y] = at(i, (R * d.score) / 100); return <circle key={i} cx={x} cy={y} r={4} strokeWidth={1.75} style={{ fill: "var(--radar-score-strong)", stroke: "var(--radar-dot-fill)" }} />; })}
      {/* dimension labels */}
      {pts.map((d, i) => {
        const [x, y] = at(i, R + 20);
        const c = Math.cos(angle(i));
        const anchor = c > 0.25 ? "start" : c < -0.25 ? "end" : "middle";
        return (
          <text key={i} x={x} y={y} textAnchor={anchor} dominantBaseline="middle" className="text-[10px] font-medium" style={{ fill: "var(--radar-label)" }}>
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}

export default function Home() {
  useSeoMeta({
    title: "SkillPatch — Discover & Share Skills for AI Agents",
    description:
      "Browse security-verified Agent skills for Claude Code, Cursor, GitHub Copilot, Codex, and any agent built on the open SKILL.md standard. Search, install, and publish skills backed by an automated 6-dimension security review.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeTab, setActiveTab] = useState(0);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    navigate(`/skills${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  }
  const scrollToVerify = () => document.getElementById("lp-verify")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="lp">

      {/* Header + footer come from the shared SiteChrome (SiteHeader / SiteFooter). */}

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-col">
          <span className="hero-pill">
            <img src="/LF_monogram.svg" alt="LatentForce" width="20" height="19" style={{ display: "block", flexShrink: 0 }} />
            <span className="mono" style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--lp-muted)" }}>A LatentForce product</span>
          </span>
          <h1 className="hero-h1">The public directory of security-verified Agent skills.</h1>
          <p className="hero-sub">Browse, search, and evaluate every skill's security review — before you sign up.</p>
          <form className="hero-search" onSubmit={onSearch}>
            <SearchIcon c="var(--text-muted)" />
            <input type="text" placeholder="Search Skills .." aria-label="Search skills" value={query} onChange={(e) => setQuery(e.target.value)} />
            <button type="submit" className="btn btn-primary">Search</button>
          </form>
          <div className="trust-stat" style={{ color: "var(--green)" }}><Shield /><span style={{ color: "var(--text-muted)" }}>Every skill passes a 6-dimension security review — hard pass/fail gate</span></div>
          <div className="hero-cta-row">
            <Link className="link-arrow" style={{ color: "var(--text-primary)" }} to="/skills">Browse all skills <ChevronRight /></Link>
            <button className="link-arrow" style={{ color: "var(--blue)" }} onClick={scrollToVerify}>How verification works <ChevronRight /></button>
          </div>
        </div>
      </section>

      {/* ── SKILL CAROUSEL ── */}
      <SkillCarousel />

      {/* ── HOW IT WORKS (interactive tabs) ── */}
      <section className="section">
        <div className="mb-10">
          <div className="eyebrow">How it works</div>
          <h2 className="lp-hiw-h2" style={{ marginTop: 12 }}>Three ways to use SkillPatch</h2>
        </div>

        {/* Tab buttons */}
        <div className="mb-10 grid gap-4 sm:grid-cols-3">
          {TABS.map((tab, i) => (
            <button
              key={tab.label}
              type="button"
              onClick={() => setActiveTab(i)}
              className="flex items-center gap-3 rounded-[12px] px-5 py-4 text-left transition-all duration-200"
              style={{
                border: activeTab === i ? "1px solid rgba(225,100,46,0.45)" : "1px solid var(--border)",
                background: activeTab === i ? "var(--brand-tint)" : "var(--bg-elev)",
              }}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]" style={{ background: activeTab === i ? "var(--brand)" : "var(--bg-subtle)", color: activeTab === i ? "#fff" : "var(--text-muted)" }}>{tab.icon}</span>
              <span className="font-display text-sm font-medium" style={{ color: activeTab === i ? "var(--brand)" : "var(--text)" }}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab content: numbered steps (left) + visual (right) */}
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start" key={activeTab}>
          <div className="relative animate-slide-up">
            <div className="absolute left-[19px] bottom-5 top-5 w-px" style={{ background: "linear-gradient(180deg, var(--brand) 0%, transparent 100%)" }} />
            <div className="space-y-8">
              {TABS[activeTab].steps.map((step, i) => (
                <div key={step.title} className="flex gap-5">
                  <div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-mono text-sm font-bold" style={{ background: "var(--brand)", color: "#fff", boxShadow: "0 0 16px var(--brand-ring)" }}>{i + 1}</div>
                  <div className="pt-1">
                    <div className="mb-1.5">
                      <span className="rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}>{step.tag}</span>
                    </div>
                    <p className="font-display font-medium" style={{ fontSize: 18, letterSpacing: "-0.01em", color: "var(--text)" }}>{step.title}</p>
                    <p className="mt-1 font-light" style={{ fontSize: 14, lineHeight: 1.8, color: "var(--text-secondary)" }}>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="animate-slide-up">
            <TabVisual tabIdx={activeTab} />
          </div>
        </div>
      </section>

      {/* ── AGENT CODE INTEGRATION ── */}
      <section className="section">
        <div className="lp-cc-wrap">
          <div className="lp-cc-copy">
            <span className="lp-cc-pill mono">Agent Agnostic Integration</span>
            <h2 className="lp-cc-h2">Use SkillPatch without leaving Agent terminal.</h2>
            <p className="lp-cc-p">Sign up, grab your setup command from Settings, run it once. From that moment Agent knows about SkillPatch — browse, install, create, and improve skills all from a single slash command.</p>
            <ol className="lp-cc-steps">
              {SETUP_STEPS.map((s) => (
                <li key={s.n}>
                  <span className="lp-cc-num">{s.n}</span>
                  <div>
                    <p className="lp-cc-step-title">{s.title}</p>
                    <p className="lp-cc-step-body">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/register" className="btn btn-primary" style={{ marginTop: 8 }}>Create account</Link>
          </div>

          {/* Terminal */}
          <div className="lp-term">
            <div className="lp-term-bar">
              <span className="lp-term-dot" style={{ background: "rgba(239,68,68,.6)" }} />
              <span className="lp-term-dot" style={{ background: "rgba(234,179,8,.6)" }} />
              <span className="lp-term-dot" style={{ background: "rgba(34,197,94,.6)" }} />
              <span className="lp-term-name">bash</span>
            </div>
            <div className="lp-term-body">
              <p><span style={{ color: "var(--lp-muted)" }}>$ </span><span style={{ color: "#fbbf24" }}>curl</span><span style={{ color: "var(--lp-muted)" }}> -sSL </span><span style={{ color: "var(--lp-text)" }}>skillpatch.dev/setup/</span><span style={{ color: "var(--lp-orange)" }}>sk_live_…</span><span style={{ color: "var(--lp-muted)" }}> | bash</span></p>
              <div className="lp-term-ok">
                <p><span style={{ color: "#4ade80" }}>✓</span> Credentials saved to ~/.zshrc</p>
                <p><span style={{ color: "#4ade80" }}>✓</span> skillpatch skill installed</p>
                <p><span style={{ color: "#4ade80" }}>✓</span> Shell environment reloaded</p>
              </div>
              <div className="lp-term-banner">
                <p>╔══════════════════════════════════════╗</p>
                <p>║  SkillPatch is ready for your Agent! ║</p>
                <p>╚══════════════════════════════════════╝</p>
              </div>
              <div className="lp-term-cmds">
                <p style={{ color: "var(--lp-muted)" }}>Open your Agent and try:</p>
                <p><span style={{ color: "#22d3ee" }}>/skillpatch browse</span><span style={{ color: "var(--lp-muted)" }}>   — explore the registry</span></p>
                <p><span style={{ color: "#22d3ee" }}>/skillpatch install</span><span style={{ color: "var(--lp-muted)" }}> &lt;slug&gt;  — install a skill</span></p>
                <p><span style={{ color: "#22d3ee" }}>/skillpatch save</span><span style={{ color: "var(--lp-muted)" }}>     — publish this session</span></p>
                <p><span style={{ color: "#22d3ee" }}>/skillpatch suggest</span><span style={{ color: "var(--lp-muted)" }}> &lt;slug&gt;  — improve a skill</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW VERIFICATION WORKS (radar + dimension list) ── */}
      <section className="section" id="lp-verify">
        <div className="mb-10">
          <div className="eyebrow">How verification works</div>
          <h2 className="lp-hiw-h2" style={{ marginTop: 12 }}>A 6-dimension security review</h2>
        </div>
        <div className="lp-verify-grid">
          <div className="lp-verify-radar"><VerifyRadar /></div>
          <ul className="lp-verify-list">
            {VERIFY_DIMS.map((d) => (
              <li key={d.label}>
                <span className="lp-verify-check"><CheckCircle /></span>
                <div>
                  <p className="lp-verify-dim">{d.label}</p>
                  <p className="lp-verify-desc">{d.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section faq-section">
        <div className="eyebrow">FAQ</div>
        <h2 className="faq-heading">Questions, answered</h2>
        <div className="faq-list">
          {FAQ.map((f, i) => (
            <div key={f.q} className={`faq-row${openFaq === i ? " open" : ""}`}>
              <button type="button" className="faq-q" aria-expanded={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)}>{f.q}<ChevronDown /></button>
              <div className="faq-a"><p>{f.a}</p></div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
